<template lang="pug">
.text-center.section
  vue-cal(view="month"
          :disable-views="['years', 'year', 'day']"
          :twelve-hour="true"
          events-on-month-view
          :on-event-click="onEventClick"
          @cell-click="onCellClick"
          @view-change="onDateUpdate"
          @ready="onDateUpdate"
          :events="events" )
</template>

<script>
import dayjs from '@/localPlugins/dayjs';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import VueCal from 'vue-cal';
import 'vue-cal/dist/vuecal.css';
import { Recurrence } from '@/libs/recurrence';

/**
 * Vue-cal documentation link
 * https://antoniandre.github.io/vue-cal-v4
 */

const DEFAULT_TARGET_TIME_TO_SCROLL_TO = 8;

export default {
  components: {
    VueCal,
  },
  props: {
    schedules: {
      type: Array,
      default: () => [],
    },
    page: {
      type: Object,
      default: () => undefined,
    },
    useNewCalendar: {
      type: Boolean,
      default: () => false,
    },
    period: {
      type: Object,
      default: () => ({ month: 1, year: 2025 }),
    },
  },
  emits: ['change', 'item-click', 'cell-click'],
  computed: {
    events() {
      let non_recurring = this.schedules.filter(job => isNil(job.recurrence_id));
      const recurring = this.schedules.filter(job => !isNil(job.recurrence_id));

      // Recurring item expansion
      const expanded = flatten(recurring.map(job => {
        // Make sure to remove nulls and id
        const pa = job.recurrence;

        const ruleSet = new Recurrence();

        // Get the period of recurrence
        // Format month with leading zero to ensure valid ISO format (YYYY-MM-DD)
        const monthLen = 2;
        const monthStr = `${this.period.month}`.padStart(monthLen, '0');
        const baseDate = dayjs(`${this.period.year}-${monthStr}-01`, 'YYYY-MM-DD');
        let startDate = baseDate.toDate();
        let endDate = baseDate.clone().add(1, 'M').toDate();
        if (!isNil(this.calendarUIMetaData)) {
          startDate = this.calendarUIMetaData.startDate;
          endDate = this.calendarUIMetaData.endDate;
        }

        // Get excluded dates
        const exdates = get(job.recurrence, 'exclude', []);
        ruleSet.addRule(pa);
        exdates.forEach(excludeDate => {
          // Add skip dates
          ruleSet.exdate(excludeDate);
        });

        // Prepare for overridden data
        const overriddenData = get(job.recurrence, 'override_data', {});

        return ruleSet.between(startDate, endDate).map(newDateTime => {
          const newJob = cloneDeep(job);
          // Recurring item's id should be unique
          // To find the item again, form the id
          // with job.id|dateTime

          const isoString = newDateTime.toISOString();
          newJob.id = `${job.id}|${isoString}`;
          if (has(overriddenData, isoString)) {
            const dataToOverride = overriddenData[isoString];
            newJob.title = get(dataToOverride, 'title', newJob.title);
            newJob.description = get(dataToOverride, 'description', newJob.description);
            newJob.execute_at = get(dataToOverride, 'execute_at', isoString);
          } else {
            newJob.execute_at = isoString;
          }
          newJob.integration_type = 'recurrence';
          return newJob;
        });
      }));

      // Merge expanded items with normal events
      non_recurring = [ ...non_recurring, ...expanded ];

      // Show different fields for integrations and internal items.
      return non_recurring.map(job => {
        if (job.integration_type === 'ms-outlook') {
          return {
            id: job.id,
            start: dayjs(job.starts_at).toDate(),
            end: dayjs(job.ends_at).toDate(),
            title: job.title,
            class: 'ms-outlook',
          };
        } else {
          const ret = {
            id: job.id,
            start: dayjs(job.execute_at).toDate(),
            // Unable to display if start & end time is same
            // Add 1 minute to display in calendar
            end: dayjs(job.execute_at).add(1, 'm').toDate(),
            title: job.title,
            class: 'reminder',
          };


          if (job.integration_type === 'recurrence') {
            ret.class = 'recurring_reminder';
          }

          return ret;
        }
      });
    },
  },
  methods: {
    onEventClick(ev) {
      if (ev.class === 'recurring_reminder') {
        // For recurring item, return the orignal item for now.
        const [ id, recurringTime ] = ev.id.split('|');
        const originalItemId = parseInt(id);
        const schedule = cloneDeep(this.schedules.find(s => s.id === originalItemId));

        schedule.execute_at = ev.start.toISOString();
        const overrideData = get(schedule, 'recurrence.override_data', {})[recurringTime];
        schedule.title = get(overrideData, 'title', schedule.title);
        schedule.description = get(overrideData, 'description', schedule.description);

        this.$emit('item-click', {
          eventType: 'recurring',
          schedule,
          recurringTime,
        });
      } else {
        this.$emit('item-click', {
          eventType: 'single',
          schedule: this.schedules.find(s => s.id === ev.id),
        });
      }
    },
    onCellClick(cell) {
      let dateTime;

      if (this.currentView === 'week') {
        dateTime = dayjs(new Date(cell));
      } else {
        const clickedDate = dayjs(new Date(cell));
        const now = dayjs();
        dateTime = clickedDate.set({
          hour: now.hour(),
          minute: 0,
          second: 0,
          millisecond: 0,
        });
      }

      this.$emit('cell-click', {
        date: dateTime.toDate(),
        time: dateTime.format('HH:mm'),
        dateTime: dateTime.toISOString(),
      });
    },
    onDateUpdate(data) {
      this.calendarUIMetaData = data;

      if (data && data.view) {
        this.currentView = data.view;

        if (data.view === 'week') {
          this.$nextTick(() => {
            this.scrollToTime(DEFAULT_TARGET_TIME_TO_SCROLL_TO);
          });
        }
      }
      // For now just get endpoint data
      const { endDate } = data;
      const m = dayjs(endDate);
      this.$emit('change', { month: m.month() + 1, year: m.year() });
    },
    scrollToTime(hour) {
      this.$nextTick(() => {
        const wkViewContainer = document.querySelector('.vuecal--week-view .vuecal__bg');
        const timeCellLabels = document.querySelectorAll('.vuecal--week-view .vuecal__time-cell-label');
        if (wkViewContainer && timeCellLabels && timeCellLabels.length > hour) {
          wkViewContainer.scrollTo({'top': timeCellLabels[hour].offsetTop, 'behavior': 'smooth'});
        }
      });
    },
  },
  data() {
    return {
      masks: {
        weekdays: 'WWW',
      },
      calendarUIMetaData: null,
      currentView: 'month',
    };
  },
  mounted() {
    if (this.currentView === 'week') {
      this.scrollToTime(DEFAULT_TARGET_TIME_TO_SCROLL_TO);
    }
  },
};
</script>

<style lang="scss">
.vuecal__menu {
  background: #4671B3;
  color: white;
}

.vuecal__event {
  margin: 2px;
  cursor: pointer;
  color: #fff;
  min-height: 20px;
}

.vuecal__event.reminder {
  background: #30C28B;
}

.vuecal__event.recurring_reminder {
  background: #30C28B;
}

.vuecal__event.ms-outlook{
  background: #00A3EE;
}

</style>
